// The four destinations' view models, minus React: worker snapshots in, rows out. Pure — the same
// snapshot and clock always give the same rows — so they are tested without a DOM. The rules live
// in the ported core (ThreadsDerivation, RecentMentions, ReactionActivity, DraftsPresentation,
// SearchResultsPresentation); this file only assembles them the way the Swift views do.
import type {
  DraftsSnapshot,
  MentionsServerResult,
  MentionsSnapshot,
  PlaceInfo,
  PostsSnapshot,
  ReactionsSnapshot,
  ThreadsSnapshot,
} from '../../../data/features/destinations';
import { messagePreview, userDisplayName } from '../../../core/channelListDerivation';
import { DraftsPresentation, type DraftRow } from '../../../core/draftsPresentation';
import { ReactionActivity, type ReactionActivityRow } from '../../../core/reactionActivity';
import type { PostRecord, UserRecord } from '../../../core/records';
import { emptyParsed } from '../../../core/searchQuery';
import { snippet, type TextRange } from '../../../core/searchResultsPresentation';
import { ThreadsAvailability, ThreadsDerivation, type DisplayThread } from '../../../core/threadsDerivation';
import { RelativeTime } from '../../../core/relativeTime';
import { t, tf } from '../../../i18n/i18n';

// MARK: - Threads

/** One Hilos row: ThreadsDerivation's DisplayThread plus the root, drawn on its own line. */
export interface ThreadRow extends DisplayThread {
  placeType: string;
  /** Names for the stacked faces' initials (an unknown person draws as "?"). */
  avatarNames: Record<string, string>;
  rootAuthorId: string;
  rootAuthorName: string;
  /** The root, flattened, no prefix (the author is drawn beside it). */
  rootText: string;
  /** The newest cached reply, prefixed ("Ana: …", "Tú: …"); null when no reply is cached. */
  replyPreview: string | null;
  timeLabel: string;
}

export function isThreadUnread(row: Pick<DisplayThread, 'unreadReplies' | 'unreadMentions'>): boolean {
  return ThreadsDerivation.threadIsUnread(row as DisplayThread);
}

export function buildThreadRows(snapshot: ThreadsSnapshot, now: number, timeZone?: string): ThreadRow[] {
  const me = snapshot.currentUserId;
  const usersById = new Map<string, UserRecord>(snapshot.users.map((u) => [u.id, u]));
  const youLabel = t('channelList.preview.you');
  const attachment = t('channelList.preview.attachment');
  const nameOf = (id: string): string | null => {
    const u = usersById.get(id);
    return u ? userDisplayName(u) : null;
  };
  return snapshot.threads.map((record) => {
    const participantIds = snapshot.participants[record.id] ?? [];
    const place = snapshot.places[record.channelId];
    const reply = snapshot.latestReplies[record.id] ?? null;
    const avatarUserIds = ThreadsDerivation.avatarIds(record, participantIds);
    const avatarVersions: Record<string, number> = {};
    const avatarNames: Record<string, string> = {};
    for (const id of avatarUserIds) {
      avatarVersions[id] = usersById.get(id)?.lastPictureUpdate ?? 0;
      avatarNames[id] = nameOf(id) ?? '?';
    }
    const replyPreview = reply
      ? ThreadsDerivation.rootPreview({
          message: reply.message,
          authorName: nameOf(reply.userId),
          isOwnMessage: reply.userId === me,
          youLabel,
          attachmentPlaceholder: attachment,
        })
      : null;
    return {
      id: record.id,
      channelId: record.channelId,
      channelName: place?.name || t('threads.unknownChannel'),
      participantSummary: ThreadsDerivation.participantSummary({
        participantIds,
        currentUserId: me,
        usersById,
        unknownFallback: t('channelList.unknownUser'),
        andLabel: t('threads.participants.and'),
        moreLabel: (n) => tf('threads.participants.more', n),
      }),
      // DisplayThread.preview keeps its Swift meaning: the newest reply, else the root.
      preview:
        replyPreview ??
        ThreadsDerivation.rootPreview({
          message: record.rootMessage,
          authorName: nameOf(record.rootUserId),
          isOwnMessage: record.rootUserId === me,
          youLabel,
          attachmentPlaceholder: attachment,
        }),
      lastReplyAt: record.lastReplyAt,
      replyCount: record.replyCount,
      unreadReplies: record.unreadReplies,
      unreadMentions: record.unreadMentions,
      isUrgent: record.isUrgent,
      avatarUserIds,
      avatarVersions,
      isFollowing: record.isFollowing,
      placeType: place?.type ?? '',
      avatarNames,
      rootAuthorId: record.rootUserId,
      rootAuthorName: record.rootUserId === me ? youLabel : nameOf(record.rootUserId) ?? t('channel.reaction.unknown'),
      rootText: messagePreview({
        message: record.rootMessage,
        authorName: null,
        isOwnMessage: false,
        showsAuthorPrefix: false,
        youLabel,
        attachmentPlaceholder: attachment,
      }),
      replyPreview,
      timeLabel: RelativeTime.format(record.lastReplyAt, now, { yesterday: t('channelList.time.yesterday') }, timeZone),
    };
  });
}

/** Whether the Hilos destination exists for this reader (ThreadsAvailability's three inputs). */
export function threadsEnabled(s: Pick<ThreadsSnapshot, 'available' | 'collapsedThreadsConfig' | 'userPreference'>): boolean {
  return ThreadsAvailability.isEnabled(s.available, s.collapsedThreadsConfig, s.userPreference);
}

/** "1 respuesta" / "7 respuestas". */
export function replyCountLabel(n: number): string {
  return n === 1 ? t('channel.replyCount.one') : tf('channel.replyCount.many', n);
}

/**
 * The optimistic read state the screen applies ON the tap, before the worker's write comes back:
 * `all` = mark-all-read, `ids` = rows opened. Applied only to the snapshot it was made against —
 * a newer snapshot already carries the worker's write (or the server's newer truth).
 */
export function applyOptimisticRead(rows: ThreadRow[], read: { all: boolean; ids: ReadonlySet<string> }): ThreadRow[] {
  if (read.all) return ThreadsDerivation.markAllRead(rows) as ThreadRow[];
  if (!read.ids.size) return rows;
  return rows.map((r) => (read.ids.has(r.id) ? { ...r, unreadReplies: 0, unreadMentions: 0 } : r));
}

// MARK: - Results (mentions, saved)

export interface ResultRow {
  postId: string;
  channelId: string;
  userId: string;
  authorName: string;
  authorVersion: number;
  place: string;
  placeType: string;
  timeLabel: string;
  text: string;
  highlights: TextRange[];
}

/** The parse a mention highlight runs on: the keys as phrases (a handle keeps its "@"). */
function keysQuery(keys: readonly string[]) {
  return { ...emptyParsed(), phrases: keys.filter((k) => k.trim() !== '') };
}

export function buildResultRows(snapshot: PostsSnapshot, now: number, opts: { keys?: readonly string[]; timeZone?: string } = {}): ResultRow[] {
  const usersById = new Map(snapshot.users.map((u) => [u.id, u]));
  const query = keysQuery(opts.keys ?? []);
  const attachment = t('channelList.preview.attachment');
  return snapshot.posts.map((post) => {
    const author = usersById.get(post.userId);
    const flat = messagePreview({
      message: post.message,
      authorName: null,
      isOwnMessage: false,
      showsAuthorPrefix: false,
      youLabel: '',
      attachmentPlaceholder: attachment,
    });
    const windowed = query.phrases.length ? snippet(flat, query) : { text: flat, highlights: [] };
    const place: PlaceInfo | undefined = snapshot.places[post.channelId];
    return {
      postId: post.id,
      channelId: post.channelId,
      userId: post.userId,
      authorName: author ? userDisplayName(author) : t('channel.reaction.unknown'),
      authorVersion: author?.lastPictureUpdate ?? 0,
      place: place?.name || t('mentions.unknownChannel'),
      placeType: place?.type ?? '',
      timeLabel: RelativeTime.format(post.createAt, now, { yesterday: t('channelList.time.yesterday') }, opts.timeZone),
      text: windowed.text,
      highlights: windowed.highlights,
    };
  });
}

/** What the mentions list says about where its rows came from. */
export type MentionsNotice = 'none' | 'offline' | 'cachedOnly';

/**
 * The two passes, combined (shared/API.md §23.1/§23.3). The server is the wider truth and its
 * answer replaces the cached one — except that an EMPTY server answer never wipes what the
 * device found, and a message newer than anything the server returned (sent seconds ago, not
 * indexed yet) stays on top. A failed server pass keeps the cache and says so.
 */
export function mergeMentions(
  local: MentionsSnapshot | undefined,
  server: MentionsServerResult | null,
  limit = 60,
): { snapshot: PostsSnapshot; notice: MentionsNotice } {
  const localSnap: PostsSnapshot = local ?? { posts: [], users: [], places: {} };
  if (!server) return { snapshot: localSnap, notice: 'none' };
  if (!server.ok) return { snapshot: localSnap, notice: 'offline' };
  if (!server.posts.length) return { snapshot: localSnap, notice: localSnap.posts.length ? 'cachedOnly' : 'none' };
  const newestServed = Math.max(...server.posts.map((p) => p.createAt));
  const seen = new Set(server.posts.map((p) => p.id));
  const fresher = localSnap.posts.filter((p) => !seen.has(p.id) && p.createAt > newestServed);
  const posts: PostRecord[] = [...fresher, ...server.posts].sort((a, b) => b.createAt - a.createAt).slice(0, limit);
  const users = new Map<string, UserRecord>();
  for (const u of [...server.users, ...localSnap.users]) if (!users.has(u.id)) users.set(u.id, u);
  return {
    snapshot: { posts, users: [...users.values()], places: { ...server.places, ...localSnap.places } },
    notice: 'none',
  };
}

// MARK: - Reactions, drafts

export function buildReactionRows(snapshot: ReactionsSnapshot, now: number, timeZone?: string): ReactionActivityRow[] {
  const names = new Map(Object.entries(snapshot.places).map(([id, p]) => [id, p.name]));
  return ReactionActivity.rows({
    reactions: snapshot.reactions,
    postsById: new Map(snapshot.posts.map((p) => [p.id, p])),
    usersById: new Map(snapshot.users.map((u) => [u.id, u])),
    channelNames: names,
    now,
    yesterdayLabel: t('channelList.time.yesterday'),
    attachmentPlaceholder: t('channelList.preview.attachment'),
    unknownActorLabel: t('channel.reaction.unknown'),
    unknownChannelLabel: t('mentions.unknownChannel'),
    timeZone,
  });
}

export interface DraftListRow extends DraftRow {
  placeType: string;
  title: string;
}

export function buildDraftRows(snapshot: DraftsSnapshot, now: number, timeZone?: string): DraftListRow[] {
  const names = new Map(Object.entries(snapshot.places).map(([id, p]) => [id, p.name]));
  return DraftsPresentation.rows({
    drafts: snapshot.drafts,
    conversationNames: names,
    now,
    yesterdayLabel: t('channelList.time.yesterday'),
    unknownConversationLabel: t('drafts.unknownConversation'),
    timeZone,
  }).map((row) => ({
    ...row,
    placeType: snapshot.places[row.channelId]?.type ?? '',
    title: row.isThreadReply ? tf('drafts.threadIn', row.conversationName) : row.conversationName,
  }));
}
